// CC-02 (B2) · las piezas del formulario de autoridades por país (AT S3 §11.3.1).
//
// Las usa `screens/AutoridadesYPlazos.tsx`: el estado de cada autoridad, el alta y la
// confirmación de los dos actos que cambian lo que reciben los casos nuevos (desactivar y
// marcar como predeterminada). Confirmar y reactivar no piden confirmación: no quitan nada.
//
// No hay «Eliminar»: una autoridad no se borra, se desactiva (RN-72, CC2-23-C8).
import { useEffect, useState } from 'react';
import { AlertTriangle, Landmark, Plus, PowerOff, Star } from 'lucide-react';
import { Alert, Button, Field, Input, Modal, Select, StatusBadge } from './ui';
import * as api from '../api/autoridades';
import type { ActoSobreLaAutoridad } from '../api/autoridades';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import type { RespuestaDelApi } from '../tipos';
import { CODIGO_DE_AUTORIDAD, predeterminadaDe } from '../lib/autoridadesDelPais';

const nombreDelPais = (pais: RespuestaDelApi | undefined): string =>
  String(pais?.nombre_pais ?? pais?.pais ?? '');

/** Predeterminada, desactivada, activa y «Por confirmar con ILENDER», siempre con texto. */
export function EstadoDeLaAutoridad({ fila }: { fila: RespuestaDelApi }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="autoridades-fila-estado">
      {fila.es_predeterminada && <StatusBadge tone="info" icon={Star}>Predeterminada</StatusBadge>}
      {fila.activa === false
        ? <StatusBadge tone="neutral" icon={PowerOff}>Desactivada</StatusBadge>
        : !fila.es_predeterminada && <StatusBadge tone="success">Activa</StatusBadge>}
      {fila.por_confirmar && <StatusBadge tone="warning">Por confirmar con ILENDER</StatusBadge>}
    </div>
  );
}

type Alta = { pais: string; codigo: string; nombre: string };
const ALTA_VACIA: Alta = { pais: '', codigo: '', nombre: '' };

const problemasDelAlta = (alta: Alta): Record<string, string> => {
  const problemas: Record<string, string> = {};
  if (!alta.pais) problemas.pais = 'Elige el país.';
  if (!alta.codigo.trim()) problemas.codigo = 'Escribe el código de la autoridad.';
  else if (!CODIGO_DE_AUTORIDAD.test(alta.codigo.trim())) {
    problemas.codigo = 'Usa de 2 a 20 letras, dígitos o guion bajo, sin espacios (SAG_ACUA).';
  }
  if (!alta.nombre.trim()) problemas.nombre = 'Escribe el nombre de la autoridad.';
  return problemas;
};

/** CC2-23-C1, C2. Código y nombre; nace sin plazos y con la firma de la predeterminada. */
export function ModalDeAltaDeAutoridad({ abierto, paises, onCerrar, onCreada }: {
  abierto: boolean;
  paises: RespuestaDelApi[];
  onCerrar: () => void;
  onCreada: (creada: RespuestaDelApi) => Promise<void> | void;
}) {
  const [alta, setAlta] = useState<Alta>(ALTA_VACIA);
  const [problemas, setProblemas] = useState<Record<string, string>>({});
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [guardando, setGuardando] = useState(false);
  useEffect(() => {
    if (abierto) { setAlta(ALTA_VACIA); setProblemas({}); setFallo(null); }
  }, [abierto]);

  const pais = paises.find((p) => p.pais === alta.pais);
  const predeterminada = predeterminadaDe(pais);
  const cambiar = (cambio: Partial<Alta>) => setAlta((a) => ({ ...a, ...cambio }));

  const guardar = async () => {
    const encontrados = problemasDelAlta(alta);
    setProblemas(encontrados);
    if (Object.keys(encontrados).length > 0) return;
    setGuardando(true);
    setFallo(null);
    try {
      const creada = await api.darDeAltaAutoridad(alta.pais, {
        codigo: alta.codigo.trim(), nombre: alta.nombre.trim(),
      });
      await onCreada(creada);
    } catch (error) {
      const f = aFallo(error);
      // CC2-23-C2: «Perú ya tiene una autoridad con el código SANIPES» va bajo el código.
      if (f.estado === 422 && /código/i.test(f.message)) setProblemas({ codigo: f.message });
      else setFallo(f);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal
      open={abierto}
      onClose={onCerrar}
      sucio={Boolean(alta.codigo || alta.nombre)}
      eyebrow="Administración · Autoridades y plazos"
      title="Agregar autoridad"
      icon={Plus}
      data-testid="autoridades-modal-alta"
      footer={(
        <>
          <Button variant="secondary" onClick={onCerrar}>Cancelar</Button>
          <Button onClick={() => { guardar(); }} disabled={guardando} data-testid="autoridades-alta-guardar">
            {guardando ? 'Agregando…' : 'Agregar autoridad'}
          </Button>
        </>
      )}
    >
      {fallo && (
        <Alert tone="danger" title="No se pudo agregar la autoridad" detalleTecnico={fallo.detalleTecnico} data-testid="autoridades-alta-error">
          {fallo.message}
        </Alert>
      )}
      <div className="space-y-4 mt-2">
        <Field label="País" required error={problemas.pais}>
          <Select
            value={alta.pais}
            placeholder="Elige el país"
            onChange={(e) => cambiar({ pais: String(e.target.value ?? '') })}
            options={paises.map((p) => ({ value: String(p.pais), label: `${nombreDelPais(p)} (${p.pais})` }))}
            data-testid="autoridades-alta-pais"
          />
        </Field>
        <Field label="Código" required hint="De 2 a 20 letras, dígitos o guion bajo. No se puede cambiar después." error={problemas.codigo}>
          <Input
            value={alta.codigo}
            maxLength={20}
            onChange={(e) => cambiar({ codigo: e.target.value.toUpperCase() })}
            data-testid="autoridades-alta-codigo"
          />
        </Field>
        <Field label="Nombre" required hint="Encabeza la carta y el Formato Amarillo." error={problemas.nombre}>
          <Input
            value={alta.nombre}
            maxLength={200}
            onChange={(e) => cambiar({ nombre: e.target.value })}
            data-testid="autoridades-alta-nombre"
          />
        </Field>
        {predeterminada && (
          <Alert tone="info" title={`Nace con la firma de ${predeterminada.nombre}`} icon={Landmark}>
            {predeterminada.texto_de_firma ?? (predeterminada.exige_profesional_responsable ? 'Exige firma del PRFV' : 'Firma interna')}.
            {' '}Nace sin plazos: mientras no los cargues, se usa el de {predeterminada.nombre}.
          </Alert>
        )}
      </div>
    </Modal>
  );
}

/** Los dos actos que se confirman, con su pregunta y su consecuencia. */
const CONFIRMACIONES: Partial<Record<ActoSobreLaAutoridad, {
  titulo: (f: RespuestaDelApi) => string;
  cuerpo: (f: RespuestaDelApi) => string;
  boton: string;
  enCurso: string;
}>> = {
  desactivar: {
    titulo: (f) => `¿Desactivar ${f.autoridad}?`,
    cuerpo: () => 'Deja de proponerse y de ofrecerse como destino en los casos nuevos. Las cartas que ya la tienen no cambian. No se borra: se puede reactivar.',
    boton: 'Sí, desactivar',
    enCurso: 'Desactivando…',
  },
  marcar_predeterminada: {
    titulo: (f) => `¿Marcar ${f.autoridad} como predeterminada de ${nombreDelPais(f)}?`,
    cuerpo: (f) => `Recibirá la carta cuando ningún producto del caso tenga registro ante otra autoridad, y prestará su plantilla y su plazo a las demás de ${nombreDelPais(f)}. Los casos ya calculados no cambian.`,
    boton: 'Sí, marcar como predeterminada',
    enCurso: 'Marcando…',
  },
};

export const ACTO_QUE_SE_CONFIRMA = (acto: ActoSobreLaAutoridad): boolean => Boolean(CONFIRMACIONES[acto]);

/**
 * Confirmación modal antes de desactivar o de cambiar la predeterminada. Foco en «Cancelar».
 * Al marcar como predeterminada una autoridad sin días de acuse, muestra el aviso que manda el
 * servidor (`aviso_al_marcar_predeterminada`): el cargo de sus cartas no se vigilaría.
 */
export function ModalDeActoSobreAutoridad({ pendiente, onCerrar, onHecho }: {
  pendiente: { fila: RespuestaDelApi; acto: ActoSobreLaAutoridad } | null;
  onCerrar: () => void;
  onHecho: (resultado: RespuestaDelApi, acto: ActoSobreLaAutoridad) => Promise<void> | void;
}) {
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [enviando, setEnviando] = useState(false);
  useEffect(() => { setFallo(null); }, [pendiente]);
  const textos = pendiente ? CONFIRMACIONES[pendiente.acto] : undefined;

  const confirmar = async () => {
    if (!pendiente) return;
    setEnviando(true);
    setFallo(null);
    try {
      const resultado = await api.cambiarEstadoDeAutoridad(
        String(pendiente.fila.pais), String(pendiente.fila.codigo), pendiente.acto,
      );
      await onHecho(resultado, pendiente.acto);
    } catch (error) {
      setFallo(aFallo(error));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal
      open={Boolean(pendiente && textos)}
      onClose={onCerrar}
      tone="warning"
      icon={pendiente?.acto === 'desactivar' ? PowerOff : Star}
      eyebrow="Administración · Autoridades y plazos"
      title={pendiente && textos ? textos.titulo(pendiente.fila) : ''}
      data-testid="autoridades-modal-acto"
      footer={(
        <>
          <Button variant="secondary" onClick={onCerrar}>Cancelar</Button>
          <Button onClick={() => { confirmar(); }} disabled={enviando} data-testid="autoridades-confirmar-acto">
            {enviando ? textos?.enCurso : textos?.boton}
          </Button>
        </>
      )}
    >
      <div className="space-y-3">
        <p>{pendiente && textos ? textos.cuerpo(pendiente.fila) : ''}</p>
        {/* Sin días de acuse no se bloquea: se avisa. El texto lo da el servidor. */}
        {pendiente?.acto === 'marcar_predeterminada' && pendiente.fila.aviso_al_marcar_predeterminada && (
          <Alert tone="warning" icon={AlertTriangle} data-testid="autoridades-acto-aviso">
            {pendiente.fila.aviso_al_marcar_predeterminada}
          </Alert>
        )}
        {fallo && (
          <Alert tone="danger" title="No se pudo completar" detalleTecnico={fallo.detalleTecnico} data-testid="autoridades-acto-error">
            {fallo.message}
          </Alert>
        )}
      </div>
    </Modal>
  );
}
